import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import type { Session } from '@supabase/supabase-js';
import { catchError, firstValueFrom, from, map, Observable, of } from 'rxjs';
import { doSupabaseCall } from '../../shared/helpers/supabase.helper';
import type { IProfileApp } from '../../shared/models';
import { SupabaseService } from '../supabase/supabase';

// Sessione + profilo RBAC dell'utente loggato. Unica fonte per guard/route/UI sul ruolo.
@Injectable({ providedIn: 'root' })
export class AuthService {
  // --- private ---
  private readonly _supabase: SupabaseService = inject(SupabaseService);
  private readonly _sessionSignal: WritableSignal<Session | null> = signal<Session | null>(null);
  private readonly _profileSignal: WritableSignal<IProfileApp | null> = signal<IProfileApp | null>(
    null,
  );
  private _readyPromise: Promise<void> | null = null;

  // --- public ---
  public readonly sessionSignal: Signal<Session | null> = this._sessionSignal.asReadonly();
  public readonly profileSignal: Signal<IProfileApp | null> = this._profileSignal.asReadonly();
  // false se Supabase non è configurato (env vuote): guard aperti, fallback dev (come PromptService).
  public readonly enabled: boolean = null !== this._supabase.client;

  // Ripristina la sessione (una sola volta) e resta in ascolto dei cambi. Guard e primo
  // loadRemote DEVONO attendere questa promise prima di usare sessionSignal/profileSignal.
  public whenReady(): Promise<void> {
    if (null === this._readyPromise) {
      this._readyPromise = this._init();
    }
    return this._readyPromise;
  }

  public signIn(email: string, password: string): Observable<string | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return from(client.auth.signInWithPassword({ email, password })).pipe(
      map(({ data, error }) => {
        if (null !== error) {
          return error.message;
        }
        this._sessionSignal.set(data.session);
        return null;
      }),
    );
  }

  public signOut(): Observable<void> {
    const client = this._supabase.client;
    if (null === client) {
      return of(undefined);
    }
    return from(client.auth.signOut()).pipe(
      map(() => {
        this._sessionSignal.set(null);
        this._profileSignal.set(null);
      }),
    );
  }

  private async _init(): Promise<void> {
    const client = this._supabase.client;
    if (null === client) {
      return;
    }
    const { data } = await client.auth.getSession();
    this._sessionSignal.set(data.session);
    if (data.session) {
      await this._loadProfile(data.session.user.id);
    }
    client.auth.onAuthStateChange((_event, session) => {
      this._sessionSignal.set(session);
      if (session) {
        this._loadProfile(session.user.id);
      } else {
        this._profileSignal.set(null);
      }
    });
  }

  private async _loadProfile(userId: string): Promise<void> {
    const client = this._supabase.client;
    if (null === client) {
      return;
    }
    const profile = await firstValueFrom(
      doSupabaseCall<IProfileApp | null>(
        client
          .from('profile_app')
          .select(
            'fk_auth_users_id, fk_user_app_id, fk_store_id, denomination, role_app(role_assigned)',
          )
          .eq('fk_auth_users_id', userId)
          .maybeSingle()
          .returns<IProfileApp>(),
      ).pipe(catchError(() => of(null))),
    );
    this._profileSignal.set(profile ?? null);
  }
}
